// src/pages/CreateCustomTemplate.js
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import FormulaBuilderModal from '../components/FormulaBuilderModal'
import './CreateCustomTemplate.css'

const API = process.env.REACT_APP_API_URL || ''

const MANDATORY_FIELDS = [
  { key: 'productName',              label: 'Product Name',           type: 'text' },
  { key: 'periodExact',              label: 'Date',                   type: 'date' },
  { key: 'totalCostsKesPerKg',       label: 'Total Cost (Kes/Kg)',    type: 'computed', formula: '' },
  { key: 'sellingPriceKesPerKg',     label: 'Selling Price (Kes/Kg)', type: 'number' },
  { key: 'grossProfitMarginPercent', label: 'Gross Profit (%)',       type: 'computed', formula: '' }
]

export default function CreateCustomTemplate() {
  const { id }    = useParams()
  const isEdit    = Boolean(id)
  const { state } = useLocation()
  const clone     = state?.clone
  const nav       = useNavigate()
  const fieldRefs = useRef({})

  // Template metadata + fields
  const [name, setName]         = useState('')
  const [currency, setCurrency] = useState('USD')
  const [fields, setFields]     = useState([...MANDATORY_FIELDS])

  // New/edit‐field form
  const [newField, setNewField] = useState({
    key:     '',
    label:   '',
    type:    'text',
    formula: '',
    options: []    // now an array, not CSV
  })
  const [editingIndex, setEditingIndex]            = useState(null)
  const [editingFormulaKey, setEditingFormulaKey]  = useState(null)
  const [showNewFormulaModal, setShowNewFormulaModal] = useState(false)

  // Per‐option formula editor for select/dropdown
  const [optionFormulaIdx, setOptionFormulaIdx] = useState(null)

  // generate a camelCase key from any label
  function toCamelCase(str='') {
    return str
      .replace(/[^a-zA-Z0-9 ]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((w,i) => i===0
        ? w.toLowerCase()
        : w[0].toUpperCase()+w.slice(1).toLowerCase()
      ).join('')
  }

  // load existing template if editing, or clone
  useEffect(() => {
    if (!isEdit && clone) {
      setName(clone.label||'')
      setCurrency(clone.settings?.currency||'USD')
      setFields(clone.fields||[...MANDATORY_FIELDS])
    }
  }, [isEdit, clone])

  useEffect(() => {
    if (!isEdit) return
    fetch(`${API}/api/custom-templates/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(tmpl => {
        setName(tmpl.name)
        setCurrency(tmpl.settings?.currency||'USD')
        setFields(tmpl.fields||[])
      })
      .catch(()=>{
        alert('Could not load template')
        nav('/costcards/new')
      })
  },[id,isEdit,nav])

  // Add or update the custom-field
  function addOrUpdateField() {
    // Merge newField with the old field (if exists) to preserve all properties
    let oldField = editingIndex !== null ? fields[editingIndex] : {};
    const f = {
      ...oldField,
      key: newField.key,
      label: newField.label,
      type: newField.type,
      formula: newField.type === 'computed' ? newField.formula : undefined,
      options: (newField.type === 'dropdown' || newField.type === 'select') ? (Array.isArray(newField.options) ? newField.options : []) : undefined
    };
    // Remove undefined keys
    Object.keys(f).forEach(k => f[k] === undefined && delete f[k]);
    const nxt = [...fields]
    if(editingIndex!==null) nxt[editingIndex] = f
    else nxt.push(f)
    setFields(nxt)
    // Reload the field by key from the latest fields array
    const updated = nxt.find(field => field.key === f.key);
    setEditingIndex(nxt.findIndex(field => field.key === f.key));
    setNewField({
      key: updated.key,
      label: updated.label,
      type: updated.type,
      formula: updated.formula || '',
      options: (updated.type === 'dropdown' || updated.type === 'select') ? (Array.isArray(updated.options) ? updated.options : []) : []
    })
  }

  // reorder via drag/drop
  function onDragEnd(result) {
    if(!result.destination) return
    const nxt = Array.from(fields)
    const [m] = nxt.splice(result.source.index,1)
    nxt.splice(result.destination.index,0,m)
    setFields(nxt)
  }

  //--- Dropdown option handlers ---
  function addOptionRow() {
    setNewField(n=>({
      ...n,
      options:[...n.options, { value:'', label:'', formula:'' }]
    }))
  }
  function updateOptionLabel(idx, label) {
    const value = toCamelCase(label)
    setNewField(n=>{
      const opts = [...n.options]
      opts[idx] = { ...opts[idx], label, value }
      return {...n, options:opts}
    })
  }
  function removeOptionRow(idx) {
    setNewField(n=>{
      const opts = n.options.filter((_,i)=>i!==idx)
      return {...n, options:opts}
    })
  }
  function openOptionFormula(idx) {
    setOptionFormulaIdx(idx)
  }
  // When editing an option's formula, always update newField.options correctly
  function saveOptionFormula(expr) {
    setNewField(n => {
      const opts = [...n.options]
      opts[optionFormulaIdx].formula = expr
      return { ...n, options: opts }
    })
    setOptionFormulaIdx(null)
  }

  //--- Field‐level formula editor ---
  function openNewFieldFormula() {
    setShowNewFormulaModal(true)
  }
  function saveNewFieldFormula(expr) {
    setNewField(n=>({...n, formula:expr}))
    setShowNewFormulaModal(false)
  }
  function saveFieldFormula(expr) {
    setFields(fs=>fs.map(f=>
      f.key===editingFormulaKey
        ? { ...f, formula:expr }
        : f
    ))
    setEditingFormulaKey(null)
  }

  //--- Persist template ---
  async function saveTemplate() {
    if(!name.trim()) return alert('Template name is required')
    const payload = {
      name,
      key: name.replace(/\s+/g,'-').toLowerCase(),
      settings: { currency, currencyOptions:[{value:'USD',label:'USD'},{value:'EUR',label:'EUR'}] },
      fields
    }
    const url    = isEdit
      ? `${API}/api/custom-templates/${id}`
      : `${API}/api/custom-templates`
    const method = isEdit ? 'PUT' : 'POST'
    const res = await fetch(url,{
      method,
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify(payload)
    })
    if(res.status===409) {
      return alert(`A template named "${name}" already exists.`)
    }
    if(!res.ok) {
      const txt = await res.text().catch(_=>res.statusText)
      return alert(txt)
    }
    nav('/costcards/new')
  }

  // In the field editor UI, when changing type, always initialize options for dropdown/select
  function handleTypeChange(e) {
    const newType = e.target.value;
    setNewField(n => ({
      ...n,
      type: newType,
      options: (newType === 'dropdown' || newType === 'select') ? (Array.isArray(n.options) ? n.options : []) : []
    }));
  }

  // Only reset the editor if the user clicks Cancel or Add Field
  function cancel() {
    if(window.confirm('Discard changes?')) {
      setEditingIndex(null)
      setNewField({ key:'',label:'',type:'text',formula:'',options:[] })
      nav('/costcards/new')
    }
  }

  // when typing a new-field label, auto-generate the key
  function handleLabelChange(e) {
    const label = e.target.value
    setNewField(n => ({
      ...n,
      label,
      key: toCamelCase(label)
    }))
  }

  // start editing an existing field
  function startEditingField(i) {
    const f = fields[i]
    let options = f.options
    // If options is a string (CSV), convert to array
    if (typeof options === 'string' && options.trim()) {
      options = options.split(',').map(opt => ({ label: opt.trim(), value: opt.trim(), formula: '' }))
    }
    // Ensure options is always an array for dropdown/select
    if ((f.type === 'dropdown' || f.type === 'select') && !Array.isArray(options)) {
      options = []
    }
    setEditingIndex(i)
    setNewField({
      key: f.key,
      label: f.label,
      type: f.type, // always set the correct type
      formula: f.formula||'',
      options: (f.type === 'dropdown' || f.type === 'select') ? (Array.isArray(options) ? options : []) : []
    })
  }

  return (
    <div className="create-custom-template">
      <h1>{isEdit?'Edit':'Create'} Custom Template</h1>

      <div className="form-row">
        <label>Template Name:</label>
        <input
          value={name}
          onChange={e=>setName(e.target.value)}
          placeholder="e.g. Import Cost Tracker"
        />
      </div>
      <div className="form-row">
        <label>Default Currency:</label>
        <select value={currency} onChange={e=>setCurrency(e.target.value)}>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
        </select>
      </div>

      <hr />
      <h2>Fields (drag to reorder)</h2>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="fields-list">
          {provided => (
            <ul
              className="fields-list"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {fields.map((f, i) => {
                const isBuilt = MANDATORY_FIELDS.some(m => m.key === f.key)
                return (
                  <Draggable key={f.key} draggableId={f.key} index={i}>
                    {(p, s) => (
                      <li
                        ref={p.innerRef}
                        {...p.draggableProps}
                        {...p.dragHandleProps}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          marginBottom: '4px',
                          background: s.isDragging ? '#eef' : '#fff',
                          border: '1px solid #ddd',
                          borderRadius: 4,
                          ...p.draggableProps.style
                        }}
                      >
                        <strong style={{ flexGrow: 1 }}>{f.label}</strong>
                        {f.type === 'computed' && (
                          <button onClick={() => setEditingFormulaKey(f.key)}>⚙️</button>
                        )}
                        {!isBuilt && (
                          <>
                            <button onClick={() => startEditingField(i)}>✏️</button>
                            <button onClick={() => {
                              if (window.confirm('Remove this field?')) {
                                setFields(fs => fs.filter((_, j) => j !== i))
                                if (editingIndex === i) setEditingIndex(null)
                              }
                            }}>🗑️</button>
                          </>
                        )}
                      </li>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>

      {/* Field editor */}
      <div className="add-field-box">
        <h3>{editingIndex!==null?'Update Field':'Add Field'}</h3>
        <button type="button" onClick={() => { setEditingIndex(null); setNewField({ key:'',label:'',type:'text',formula:'',options:[] }) }}>+ Add New Field</button>
        <div style={{ background: '#f8fafc', color: '#64748b', fontSize: 12, padding: 8, margin: '8px 0', border: '1px solid #e2e8f0', borderRadius: 4 }}>
          <strong>DEBUG newField:</strong>
          <pre style={{ margin: 0 }}>{JSON.stringify(newField, null, 2)}</pre>
        </div>
        <div className="add-field-row">
          <label>Field Label:</label>
          <input
            className="large-input"
            placeholder="e.g. Cost per Kg"
            value={newField.label}
            onChange={handleLabelChange}
          />
        </div>
        <div className="add-field-row">
          <label>Field ID:</label>
          <input className="small-input" value={newField.key} readOnly />
        </div>
        <div className="add-field-row">
          <label>Type:</label>
          <select
            value={newField.type}
            onChange={handleTypeChange}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="computed">Formula</option>
            <option value="dropdown">Dropdown</option>
            <option value="select">Select</option>
          </select>
        </div>

        {/* Advanced Dropdown/Select Option UI */}
        {(newField.type === 'dropdown' || newField.type === 'select') && (
          <div className="dropdown-options-ui" style={{ marginTop: 12 }}>
            <table style={{ width: '100%', marginBottom: 8, borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '6px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Label</th>
                  <th style={{ padding: '6px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '6px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Formula</th>
                  <th style={{ borderBottom: '2px solid #e2e8f0' }}></th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(newField.options) && newField.options.map((opt, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '4px 8px' }}>
                      <input
                        value={opt.label}
                        onChange={e => updateOptionLabel(idx, e.target.value)}
                        placeholder="Option label"
                        style={{ width: '100%', padding: '4px', borderRadius: 4, border: '1px solid #d1d5db' }}
                      />
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <input
                        value={opt.value}
                        onChange={e => setNewField(n => {
                          const opts = [...n.options]
                          opts[idx] = { ...opts[idx], value: e.target.value }
                          return { ...n, options: opts }
                        })}
                        placeholder="Option ID"
                        style={{ width: '100%', padding: '4px', borderRadius: 4, border: '1px solid #d1d5db' }}
                      />
                    </td>
                    <td style={{ padding: '4px 8px', minWidth: 120 }}>
                      <span style={{ fontFamily: 'monospace', color: opt.formula ? '#2563eb' : '#64748b' }}>
                        {opt.formula || <span style={{ color: '#bdbdbd' }}>—</span>}
                      </span>
                      <button
                        type="button"
                        title="Edit formula"
                        style={{ marginLeft: 8, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', color: '#2563eb' }}
                        onClick={() => openOptionFormula(idx)}
                      >
                        ⚙️
                      </button>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <button type="button" onClick={() => removeOptionRow(idx)} style={{ color: '#ef4444', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              onClick={addOptionRow}
              style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, fontSize: 15, float: 'right', marginBottom: 8, cursor: 'pointer', boxShadow: '0 2px 6px rgba(37,99,235,0.08)' }}
            >
              + Add Option
            </button>
            {optionFormulaIdx !== null && (
              <FormulaBuilderModal
                field={newField.options[optionFormulaIdx]?.label || ''}
                existingExpr={newField.options[optionFormulaIdx]?.formula || ''}
                fieldsList={fields.map(f => f.key)}
                labels={Object.fromEntries(fields.map(f => [f.key, f.label]))}
                onSave={saveOptionFormula}
                onCancel={() => setOptionFormulaIdx(null)}
              />
            )}
          </div>
        )}

        {/* New field’s own formula */}
        {newField.type==='computed' && (
          <div className="add-field-row">
            <button className="btn-add-formula" onClick={openNewFieldFormula}>
              Build Formula…
            </button>
            {newField.formula && (
              <div className="formula-preview">
                <code>{newField.formula}</code>
              </div>
            )}
          </div>
        )}

        <button className="btn-add" onClick={addOrUpdateField}>
          {editingIndex!==null?'Update Field':'Add Field'}
        </button>
      </div>

      {/* Formula modals */}
      {editingFormulaKey && (
        <FormulaBuilderModal
          field={editingFormulaKey}
          existingExpr={fields.find(f=>f.key===editingFormulaKey)?.formula||''}
          fieldsList={fields.map(f=>f.key)}
          labels={Object.fromEntries(fields.map(f=>[f.key,f.label]))}
          onSave={saveFieldFormula}
          onCancel={()=>setEditingFormulaKey(null)}
        />
      )}
      {showNewFormulaModal && (
        <FormulaBuilderModal
          field={newField.key}
          existingExpr={newField.formula}
          fieldsList={fields.map(f=>f.key)}
          labels={Object.fromEntries(fields.map(f=>[f.key,f.label]))}
          onSave={saveNewFieldFormula}
          onCancel={()=>setShowNewFormulaModal(false)}
        />
      )}
      {optionFormulaIdx!==null && (
        <FormulaBuilderModal
          field={`${newField.key}_opt${optionFormulaIdx}`}
          existingExpr={newField.options[optionFormulaIdx].formula}
          fieldsList={fields.map(f=>f.key)}
          labels={Object.fromEntries(fields.map(f=>[f.key,f.label]))}
          onSave={saveOptionFormula}
          onCancel={()=>setOptionFormulaIdx(null)}
        />
      )}

      <hr/>
      <div className="form-actions">
        <button className="btn-cancel" onClick={cancel}>Cancel</button>
        <button className="btn-save" onClick={saveTemplate}>
          {isEdit?'Update Template':'Create Template'}
        </button>
      </div>
    </div>
  )
}
